import type { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser'
import client from './client'
import { useAuthStore } from '../stores/authStore'
import type { User } from '../types/auth'

interface EntraConfig {
  enabled: boolean
  tenant_id: string | null
  client_id: string | null
  local_password_login: boolean
}

let msal: PublicClientApplication | null = null
let msalInitialization: Promise<PublicClientApplication> | null = null
let redirectHandling: Promise<User | null> | null = null

async function readConfig(): Promise<EntraConfig> {
  return client.get<EntraConfig>('/auth/entra/config', { timeout: 15000 })
}

async function getMsal(config?: EntraConfig): Promise<PublicClientApplication> {
  if (msalInitialization) return msalInitialization
  if (msal) return msal
  const resolved = config ?? await readConfig()
  if (!resolved.enabled || !resolved.tenant_id || !resolved.client_id) {
    throw new Error('Microsoft sign-in is not configured. Contact the application administrator.')
  }
  const { BrowserCacheLocation, PublicClientApplication: MsalApplication } = await import('@azure/msal-browser')
  const application = new MsalApplication({
    auth: {
      clientId: resolved.client_id,
      authority: `https://login.microsoftonline.com/${resolved.tenant_id}`,
      redirectUri: `${window.location.origin}/login`,
      postLogoutRedirectUri: `${window.location.origin}/login?signed_out=1`,
    },
    cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
    system: { loggerOptions: { piiLoggingEnabled: false, loggerCallback: () => undefined } },
  })
  msal = application
  msalInitialization = application.initialize().then(() => application).catch((error) => {
    msal = null
    msalInitialization = null
    throw error
  })
  return msalInitialization
}

export async function getLoginConfig(): Promise<EntraConfig> {
  return readConfig()
}

export async function handleEntraRedirect(config: EntraConfig): Promise<User | null> {
  if (!config.enabled) return null
  if (!redirectHandling) {
    redirectHandling = (async () => {
      const application = await getMsal(config)
      const result: AuthenticationResult | null = await application.handleRedirectPromise()
      if (!result) return null

      const challenge = sessionStorage.getItem('protocol-decompiler.entra.challenge')
      if (!challenge || !result.idToken) {
        throw new Error('Microsoft sign-in expired. Please start again.')
      }
      const user = await client.post<User>('/auth/entra/session', {
        challenge,
        id_token: result.idToken,
      })
      sessionStorage.removeItem('protocol-decompiler.entra.challenge')
      if (result.account) application.setActiveAccount(result.account)
      useAuthStore.getState().setUser(user)
      return user
    })().catch((error) => {
      redirectHandling = null
      throw error
    })
  }
  return redirectHandling
}

export async function startEntraLogin(returnTo: string, config?: EntraConfig, chooseAccount = false): Promise<void> {
  const resolved = config ?? await readConfig()
  const application = await getMsal(resolved)
  const challenge = await client.post<{ challenge: string; nonce: string }>(
    '/auth/entra/challenge',
    {},
    { timeout: 15000 },
  )
  sessionStorage.setItem('protocol-decompiler.entra.challenge', challenge.challenge)
  const safeReturn = returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\')
    ? returnTo
    : '/'
  sessionStorage.setItem('protocol-decompiler.entra.returnTo', safeReturn)
  await application.loginRedirect({
    scopes: ['openid', 'profile', 'email'],
    nonce: challenge.nonce,
    ...(chooseAccount ? { prompt: 'select_account' as const } : {}),
  })
}

export function consumeEntraReturnPath(): string {
  const value = sessionStorage.getItem('protocol-decompiler.entra.returnTo') || '/'
  sessionStorage.removeItem('protocol-decompiler.entra.returnTo')
  return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\') ? value : '/'
}

export async function logoutFromEntra(): Promise<void> {
  const config = await readConfig().catch(() => null)
  if (!config?.enabled) return
  const application = await getMsal(config)
  await application.logoutRedirect({
    account: application.getActiveAccount() ?? undefined,
    postLogoutRedirectUri: `${window.location.origin}/login?signed_out=1`,
  })
}
